import React from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine } from '@remixicon/react';

/**
 * Modal Component
 * A full-screen or centered modal overlay
 */
const Modal = ({ isOpen, onClose, children, title, size = 'default', headerClassName = '', closeOnOutsideClick = true, zClassName = 'z-[200]' }) => {
  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    default: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl',
    '2xl': 'max-w-7xl',
    full: 'max-w-full mx-4'
  };

  const modalContent = (
    // `data-modal-portal` marks this subtree so a parent Radix Drawer/Dialog
    // knows a click landing here isn't an "outside" interaction and shouldn't
    // dismiss it. See Drawer.jsx's onInteractOutside guard.
    // zClassName lets callers stack this modal above a drawer that has
    // bumped its own z-index (TailDetailsDrawer uses !z-[250]).
    <div data-modal-portal="true" className={`fixed inset-0 ${zClassName} isolate flex items-center justify-center pointer-events-auto`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm pointer-events-auto"
        onClick={closeOnOutsideClick ? onClose : undefined}
      />

      {/* Modal Content */}
      <div className={`relative bg-white rounded-2xl shadow-2xl w-full ${sizeClasses[size]} max-h-[90vh] overflow-hidden flex flex-col pointer-events-auto`}>
        {/* Header */}
        {title && (
          <div className={`flex items-center justify-between px-6 py-4 border-b border-gray-200 ${headerClassName}`}>
            <h2 className="text-lg font-semibold text-primary-500">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              aria-label="Close modal"
            >
              <RiCloseLine className="w-5 h-5" />
            </button>
          </div>
        )}
        
        {/* Body */}
        <div className="flex-1 overflow-auto">
          {children}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;
